import * as React from "react"
import {
  useQuery,
  useQueries,
  useMutation,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
} from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"
import { getTodaysDate, getPastDate } from "util/formatters"

// For interacting with the React Query cache
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: process.env.NODE_ENV === "development" ? 1 : 3,
    },
  },
})

/** ------------------- **/
/*  Companies
/** ------------------- **/

export function useCompanies(params) {
  const { search, productSearch, vertical, revenue, pageSize, pageOffset } =
    params
  const splitRevenue = revenue?.split("-")
  const revenue_min = splitRevenue?.[0] ? splitRevenue[0] : undefined
  const revenue_max = splitRevenue?.[1] ? splitRevenue[1] : undefined
  const paramsString = qs.stringify({
    search: search ? search : "",
    product_term: productSearch ? productSearch : "",
    vertical: vertical ? vertical : undefined,
    revenue_min,
    revenue_max,
    page_size: pageSize ? pageSize : 25,
    page_number: pageOffset ? pageOffset : 0,
  })
  const cacheKey = ["companies", params]
  const query = () => apiRequest(`companies/list?${paramsString}`)
  return useQuery(cacheKey, query)
}

export function useCompaniesExport() {
  return useMutation(
    ({ search, productSearch, vertical, revenue, pageOffset }) => {
      const splitRevenue = revenue?.split("-")
      const revenue_min = splitRevenue?.[0] ? splitRevenue[0] : undefined
      const revenue_max = splitRevenue?.[1] ? splitRevenue[1] : undefined
      const paramsString = qs.stringify({
        search: search ? search : "",
        product_term: productSearch ? productSearch : "",
        vertical: vertical ? vertical : undefined,
        revenue_min,
        revenue_max,
        page_size: 10000,
        page_number: pageOffset ? pageOffset : 0,
      })
      return apiRequest(`companies/list?${paramsString}`)
    },
    {
      onSuccess: () => {
        toast.success("Your download has started!")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating this report")
      },
    }
  )
}

export function useCompanyDetails({ id }) {
  const cacheKey = ["companyDetails", id]
  const query = () => apiRequest(`companies/${id}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanySales({
  id,
  startDate,
  endDate,
  searchTerm = undefined,
  selectedProductTypes = "",
  selectedColors = "",
  selectedGenders = "",
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
    search: searchTerm,
    product_types: selectedProductTypes,
    colors: selectedColors,
    genders: selectedGenders,
  })
  const cacheKey = [
    "companySales",
    id,
    startDate,
    endDate,
    searchTerm,
    selectedProductTypes,
    selectedColors,
    selectedGenders,
  ]
  const query = () => apiRequest(`companies/${id}/sales?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useMultipleCompanyDetails({ companyIds }) {
  const useQueriesArray =
    companyIds?.map(companyId => ({
      queryKey: ["companyDetails", companyId],
      queryFn: () => apiRequest(`companies/${companyId}`),
    })) ?? []
  return useQueries(useQueriesArray)
}

export function useMultipleCompanySales({ companies, startDate, endDate }) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const useQueriesArray = companies?.map(company => ({
    queryKey: ["companySales", company.id, startDate, endDate],
    queryFn: () => apiRequest(`companies/${company.id}/sales?${paramsString}`),
  }))
  return useQueries(useQueriesArray)
}

export function useCompanyTopProducts({
  id,
  limit = 2,
  startDate,
  endDate,
  skipFilters = false,
  searchTerm = undefined,
  selectedProductTypes = "",
  selectedColors = "",
  selectedGenders = "",
  selectedSizes = "",
}) {
  const paramsString = qs.stringify({
    limit,
    start_date: startDate,
    end_date: endDate,
    skipFilters,
    search: searchTerm,
    product_types: selectedProductTypes,
    colors: selectedColors,
    genders: selectedGenders,
    sizes: selectedSizes,
  })
  const cacheKey = [
    "companyTopProducts",
    id,
    limit,
    startDate,
    endDate,
    skipFilters,
    searchTerm,
    selectedProductTypes,
    selectedColors,
    selectedGenders,
    selectedSizes,
  ]
  const query = () =>
    apiRequest(`companies/${id}/top_products_v2?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanyTrendingProducts({ id }) {
  const cacheKey = ["companyTrendingProducts", id]
  const query = () => apiRequest(`companies/${id}/trending_products`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompanyProductGroups({ id }) {
  const cacheKey = ["companyProductGroups", id]
  const query = () => apiRequest(`companies/${id}/product_groups`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useMarketingEvents(
  { id, startDate, endDate, marketDataType = "social" },
  enabled = true
) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
    market_data_type: marketDataType,
  })
  const cacheKey = [
    "companyMarketingEvents",
    id,
    startDate,
    endDate,
    marketDataType,
  ]
  const query = () => apiRequest(`companies/${id}/market_data?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id && !!enabled })
}

export function useMarketingMeta({
  id,
  marketDataType = "details,social,ads,traffic",
}) {
  const paramsString = qs.stringify({
    request_type: marketDataType,
  })
  const cacheKey = ["companyMarketingMeta", id, marketDataType]
  const query = () =>
    apiRequest(`companies/${id}/external_meta_data?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useCompetitorSnapshot(companyIdsArray) {
  const companyIdsString = companyIdsArray.join(",")

  const paramsString = qs.stringify({
    id: companyIdsString,
    startDate: getPastDate(7, "day"),
    endDate: getPastDate(1, "day"),
  })
  const cacheKey = ["competitorSnapshot", companyIdsString]
  const query = () => apiRequest(`companies/snapshot?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!companyIdsArray.length })
}

export function useDeleteCompany() {
  return useMutation(
    ({ groupId, companyIdToRemove }) => {
      const paramsString = qs.stringify({
        group_id: groupId,
        company_to_remove: companyIdToRemove,
      })
      return apiRequest(`group/remove_company?${paramsString}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("Comany has been removed")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue removing that company")
      },
    }
  )
}

export function useAdsRecentOrders({ groupId }) {
  const cacheKey = ["adsRecentOrders", groupId]
  const paramString = qs.stringify({
    group_id: groupId,
  })
  const query = () => apiRequest(`ads/recent_orders?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

export function useAdsOrder({ orderId, date, groupId }) {
  const cacheKey = ["adsOrder", orderId, date]
  const paramString = qs.stringify({
    order_id: orderId,
    created_at: date,
    group_id: groupId,
  })
  const query = () => apiRequest(`ads/order?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!orderId })
}

export function useAttributionOverview({ groupId, startDate, endDate }) {
  const cacheKey = ["attributionOverview", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    // start_date: startDate,
    // end_date: endDate,
  })
  const query = () => apiRequest(`ads/overview?${paramString}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

/** ------------------- **/
/*  Products
/** ------------------- **/

export function useProduct({ companyId, productId }) {
  const cacheKey = ["product", companyId, productId]
  const query = () => apiRequest(`products/${companyId}/${productId}`)
  return useQuery(cacheKey, query, { enabled: !!companyId && !!productId })
}

export function useMultipleProductDetails({ competitorProductAndCompanyIds }) {
  const useQueriesArray =
    competitorProductAndCompanyIds?.map(productAndCompanyIds => ({
      queryKey: ["product", productAndCompanyIds[0], productAndCompanyIds[1]],
      queryFn: () =>
        apiRequest(
          `products/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}`
        ),
    })) ?? []
  return useQueries(useQueriesArray)
}

export function useProductSales({ companyId, productId, startDate, endDate }) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = ["productSales", companyId, productId, startDate, endDate]
  const query = () =>
    apiRequest(`products/${companyId}/${productId}/sales?${paramsString}`)
  return useQuery(cacheKey, query, { enabled: !!companyId && !!productId })
}

export function useMultipleProductSales({
  competitorProductAndCompanyIds,
  startDate,
  endDate,
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const useQueriesArray =
    competitorProductAndCompanyIds?.map(productAndCompanyIds => ({
      queryKey: [
        "productSales",
        productAndCompanyIds[0],
        productAndCompanyIds[1],
        startDate,
        endDate,
      ],
      queryFn: () =>
        apiRequest(
          `products/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}/sales?${paramsString}`
        ),
    })) ?? []
  return useQueries(useQueriesArray)
}

export function useVerticalsList() {
  const cacheKey = ["verticalsList"]
  const query = () => apiRequest(`products/verticals`)
  return useQuery(cacheKey, query)
}

export function useSearchProducts({
  companyId,
  debouncedSearchTerm,
  pageSize,
  pageOffset,
}) {
  const paramString = qs.stringify({
    title: debouncedSearchTerm,
    company_id: companyId,
    page_size: pageSize,
    page_number: pageOffset,
  })
  const cacheKey = [
    "searchProducts",
    companyId,
    debouncedSearchTerm,
    pageSize,
    pageOffset,
  ]
  const query = () => apiRequest(`products/search?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId && debouncedSearchTerm?.length > 2,
  })
}

export function useRecentProductEvents({ companyId }) {
  const cacheKey = ["recentProductEvents", companyId]
  const query = () => apiRequest(`companies/${companyId}/recent_events`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId,
  })
}

/** ------------------- **/
/*  Users
/** ------------------- **/

export function useCurrentUser() {
  const cacheKey = ["user"]
  const query = () => apiRequest("user")
  return useQuery(cacheKey, query)
}

export function useUpdateConfirmUser() {
  return useMutation(({ confirmed }) =>
    apiRequest(`user`, "PUT", { confirmed })
  )
}

export function useUpdateUser() {
  return useMutation(
    ({ emailSub }) =>
      apiRequest(`user`, "PUT", {
        email_sub: emailSub,
      }),
    {
      onSuccess: () => {
        toast.success("Account updated successfully")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue updating your profile")
      },
    }
  )
}

export function useGroupDetails({ groupId }) {
  const cacheKey = ["groupDetails", groupId]
  const query = () => apiRequest(`group/${groupId}`)
  return useQuery(cacheKey, query, { enabled: !!groupId })
}

export function useCreateUser() {
  return useMutation(
    ({
      email,
      groupName,
      firstName,
      lastName,
      contractEndDate,
      createUserAuthToken,
      planId,
      planFeatures,
      competitorLimit,
      stripeCustomerId,
    }) => {
      return apiRequest(`group/create_new_customer`, "POST", {
        users: [
          {
            email,
            firstName,
            lastName,
            userType: "owner",
          },
        ],
        group_name: groupName,
        stripe_customer_id: stripeCustomerId,
        plan_id: planId,
        plan_features: planFeatures,
        contract_end_date: contractEndDate,
        competitor_limit: competitorLimit,
        purchased_companies: [],
        auth_token: createUserAuthToken,
      })
    },
    {
      onSuccess: () => {
        toast.success("User and group has been created")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue creating that user")
      },
    }
  )
}

export function useCreateDemoCustomer() {
  return useMutation(
    ({ users, groupName }) => {
      return apiRequest(`group/create_new_customer`, "POST", {
        users,
        purchased_companies: [],
        group_name: groupName,
        stripe_customer_id: "",
        plan_id: "demo",
        plan_features: {},
        // hardcoded token we use on the backend to verify the
        auth_token: "NMBqTFLWTj3svitxqNgSg3EO95krlLge2SxzZRpi",
      })
    },
    {
      onSuccess: () => {
        toast.success("A new demo customer has been created")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue creating a new demo customer")
      },
    }
  )
}

export function useInviteUser() {
  return useMutation(
    ({ email, role, groupId, isResend }) => {
      return apiRequest(`user`, "POST", {
        email,
        type: role,
        group_id: groupId,
        is_resend: isResend || false,
      })
    },
    {
      onSuccess: () => {
        toast.success("User has been invited")
      },
      onError: error => {
        console.error(error.message)
        if (error.message.includes("UsernameExistsException")) {
          toast.error("Hmm, looks like that user has already been invited")
        } else {
          toast.error("Hmm, there was an issue inviting that user")
        }
      },
    }
  )
}

export function useDeleteUser() {
  return useMutation(
    ({ userId, groupId }) => {
      const paramsString = qs.stringify({
        user_id: userId,
        group_id: groupId,
      })
      return apiRequest(`user?${paramsString}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("User deleted successfully")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue deleting that user")
      },
    }
  )
}

export function useDisableGroup() {
  return useMutation(
    ({ groupId }) => {
      return apiRequest(`group/offboard`, "POST", {
        group_id: groupId,
      })
    },
    {
      onSuccess: () => {
        toast.success("This group was successfully disabled")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue disabling that group")
      },
    }
  )
}

export function useGenerateApiKey() {
  return useMutation(
    () => {
      return apiRequest(`user/apitoken/get_token`, "POST")
    },
    {
      onSuccess: () => {
        toast.success("API key successfully generated")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating a key")
      },
    }
  )
}

export function useRevokeApiKey() {
  return useMutation(
    key => {
      return apiRequest(`user/apitoken/revoke_token?api_token=${key}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("API key revoked")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue revoking this key")
      },
    }
  )
}

/** ------------------- **/
/*  Internal Admin
/** ------------------- **/

export function useListGroups() {
  const cacheKey = ["listGroups"]
  const query = () => apiRequest(`group/list`)
  return useQuery(cacheKey, query)
}

export function useListPendingCompanies() {
  const cacheKey = ["listPendingCompanies"]
  const query = () => apiRequest(`group/list_pending_companies`)
  return useQuery(cacheKey, query)
}

export function useLogUserSession() {
  return useMutation(() => {
    return apiRequest(`user/log`, "POST")
  })
}

export function useVerifyPendingCompany() {
  return useMutation(
    ({ companyId, groupId, verified }) => {
      return apiRequest(`group/verify_pending_company`, "POST", {
        company_id: companyId,
        group_id: groupId,
        verified,
      })
    },
    {
      onSuccess: () => {
        toast.success("This company has been succesfully verified")
      },
      onError: error => {
        console.error(error)
        toast.error(
          `Hmm, there was an issue, check the browser console for more details.`
        )
      },
    }
  )
}

export function useAddCompany() {
  return useMutation(
    ({ groupId, companyToAdd }) => {
      return apiRequest(`group/add_company`, "POST", {
        group_id: groupId,
        company_to_add: companyToAdd,
      })
    },
    {
      onSuccess: () => {
        toast.success("Company has been added successfully")
      },
      onError: error => {
        toast.error(error.message)
      },
    }
  )
}

export function useRemoveCompany() {
  return useMutation(
    ({ groupId, companyToRemove }) => {
      const paramsString = qs.stringify({
        group_id: groupId,
        company_to_remove: companyToRemove,
      })
      return apiRequest(`group/remove_company?${paramsString}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("Company was removed successfully")
      },
      onError: error => {
        console.error(error)
        if (error.message) {
          toast.error(error.message)
        } else {
          toast.error("Hmm, there was an issue removing that company")
        }
      },
    }
  )
}

export function useSetShopifyIntegration() {
  return useMutation(
    ({ groupId, shopName, apiAccessToken }) => {
      return apiRequest(`group/set_shopify_integration`, "POST", {
        group_id: groupId,
        dev_store_name: shopName,
        access_token: apiAccessToken,
      })
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your shopify integration data"
        )
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue sending your shopify integration data"
        )
      },
    }
  )
}

export function useSetPixelIntegration() {
  return useMutation(
    ({ groupId, site }) => {
      return apiRequest(`group/luz_pixel_verification`, "POST", {
        group_id: groupId,
        site,
      })
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Luz Pixel integration"
        )
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue with your Luz Pixel integration, please try again"
        )
      },
    }
  )
}

export function useAuthKlaviyo() {
  return useMutation(
    ({ groupId, klavAPIKey }) => {
      return apiRequest(
        `group/klaviyo_login?group_id=${groupId}&klav_api_key=${klavAPIKey}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Klaviyo API key and will begin importing data"
        )
      },
      onError: error => {
        console.error(error)
        if (error.message.includes("Invalid API key")) {
          toast.error("Hmm, we couldn't validate that Klaviyo API key")
        } else if (error.message.includes("InvalidRequestException")) {
          toast.error(
            "Hmm, something went wrong finding a place to store your key, please try again in a few minutes"
          )
        } else {
          toast.error(
            "Hmm, there was an issue sending your Klaviyo API key to our servers. Please try again later or contact support"
          )
        }
      },
    }
  )
}

/** ------------------- **/
/*  Internal Tools
/** ------------------- **/

export function useHighPriorityCompanies() {
  const cacheKey = ["highPriorityCompanies"]
  const query = () => apiRequest(`companies/list_purchased_companies`)
  return useQuery(cacheKey, query)
}

export function useDataQuality() {
  const cacheKey = ["dataQuality"]
  const query = () =>
    apiRequest(`api/dataQuality`, "GET", undefined, window?.origin)
  return useQuery(cacheKey, query)
}

export function useCompanyDataQuality(id) {
  const cacheKey = ["dataQuality", id]
  const query = () =>
    apiRequest(
      `api/dataQuality/company/${id}`,
      "GET",
      undefined,
      window?.origin
    )
  return useQuery(cacheKey, query, { enabled: !!id })
}

export function useInternalCompanySearch(searchTerm) {
  const cacheKey = ["internalCompanySearch", searchTerm]
  const query = () =>
    apiRequest(
      `api/dataQuality/company/search?term=${searchTerm}`,
      "GET",
      undefined,
      window?.origin
    )
  return useQuery(cacheKey, query, { enabled: !!searchTerm })
}

export function useTodaysAlerts() {
  const cacheKey = ["todaysAlerts"]
  const currentDate = getTodaysDate()
  const query = () =>
    apiRequest(
      `api/dataQuality/today?currentDate=${currentDate}`,
      "GET",
      undefined,
      window?.origin
    )
  return useQuery(cacheKey, query)
}

// React Query context provider that wraps our app
export function QueryClientProvider(props) {
  return (
    <QueryClientProviderBase client={queryClient}>
      {props.children}
    </QueryClientProviderBase>
  )
}

export function useOpenSearchCompaniesQuery() {
  const cacheKey = ["openSearchHighPriorityCompanies"]
  const query = () =>
    apiRequest(
      `_plugins/_sql`,
      "POST",
      {
        fetch_size: 10000,
        query: "select * from companies-index where priority='high'",
      },
      `https://search-product-search-5hwivt37ed6vnywtkc3nemoaey.us-east-1.es.amazonaws.com`,
      "Basic " + btoa("productAdmin:Luz2022$")
    )
  return useQuery(cacheKey, query)
}

export function useDailyValidationStatistics(date) {
  const cacheKey = ["dailyValidationStatistics", date]
  const paramString = qs.stringify({
    date,
  })
  const query = () =>
    apiRequest(`companies/daily_validation_statistics?${paramString}`)
  return useQuery(cacheKey, query)
}

/** ------------------- **/
/*  Reports
/** ------------------- **/

export function useGenerateReport() {
  return useMutation(
    ({ companyId, groupId, userId, startDate, endDate }) => {
      const paramString = qs.stringify({
        company_id: companyId,
        group_id: groupId,
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
      })
      return apiRequest(`companies/${companyId}/company_export?${paramString}`)
    },
    {
      onSuccess: () => {
        toast.success("Your report has started being processed")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating that report")
      },
    }
  )
}

export function useListReports({ companyId, groupId, userId }) {
  const cacheKey = ["listReports", companyId, groupId, userId]
  const paramString = qs.stringify({
    company_id: companyId,
    group_id: groupId,
    user_id: userId,
  })
  const query = () => apiRequest(`user/list_company_reports?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId && !!groupId && !!userId,
  })
}

export function useDownloadReport() {
  return useMutation(
    ({ companyId, groupId, userId, startDate, endDate }) => {
      const paramString = qs.stringify({
        company_id: companyId,
        group_id: groupId,
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
      })
      return apiRequest(`user/download_company_report?${paramString}`)
    },
    {
      onSuccess: () => {
        toast.success("This report was successfully downloaded")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue downloading that report")
      },
    }
  )
}

export function useProductReport() {
  return useMutation(
    ({ companyId, productIds, startDate, endDate }) => {
      const paramsString = qs.stringify({
        start_date: startDate,
        end_date: endDate,
        product_ids: productIds,
      })
      return apiRequest(
        `companies/${companyId}/competitor_report?${paramsString}`
      )
    },
    {
      onSuccess: () => {
        toast.success("Your download has started!")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating this report")
      },
    }
  )
}

/** ------------------- **/
/*  Shopify data
/** ------------------- **/

export function useGetMerchantSales({ groupId, startDate, endDate }) {
  const cacheKey = ["merchantSales", groupId, startDate, endDate]
  const paramString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`group/${groupId}/sales?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!startDate && !!endDate,
  })
}

export function useGetMerchantProductDetails({ groupId, productId }) {
  const cacheKey = ["merchantProductDetails", groupId, productId]
  const query = () => apiRequest(`group/${groupId}/${productId}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!productId,
  })
}

export function useGetMerchantProductSales({
  productId,
  groupId,
  startDate,
  endDate,
}) {
  const paramsString = qs.stringify({
    start_date: startDate,
    end_date: endDate,
  })
  const cacheKey = [
    "merchantProductSales",
    groupId,
    productId,
    startDate,
    endDate,
  ]
  const query = () =>
    apiRequest(`group/${groupId}/${productId}/sales?${paramsString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && !!productId,
  })
}

export function useMultipleGroupProductDetails({
  competitorProductAndCompanyIds,
}) {
  const useQueriesArray =
    competitorProductAndCompanyIds?.map(productAndCompanyIds => ({
      queryKey: [
        "merchantProductDetails",
        productAndCompanyIds[0],
        productAndCompanyIds[1],
      ],
      queryFn: () =>
        apiRequest(
          `group/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}`
        ),
    })) ?? []
  return useQueries(useQueriesArray)
}

export function useMultipleGroupProductSales({
  groupProductAndCompanyIds,
  startDate,
  endDate,
}) {
  const useQueriesArray =
    groupProductAndCompanyIds?.map(productAndCompanyIds => {
      const paramsString = qs.stringify({
        start_date: startDate,
        end_date: endDate,
      })
      return {
        queryKey: [
          "merchantProductSales",
          productAndCompanyIds[0],
          productAndCompanyIds[1],
          startDate,
          endDate,
        ],
        queryFn: () =>
          apiRequest(
            `group/${productAndCompanyIds[0]}/${productAndCompanyIds[1]}/sales?${paramsString}`
          ),
      }
    }) ?? []
  return useQueries(useQueriesArray)
}

export function useSearchGroupProducts({
  groupId,
  debouncedSearchTerm,
  pageSize,
  pageOffset,
}) {
  const paramString = qs.stringify({
    title: debouncedSearchTerm,
    group_id: groupId,
    page_size: pageSize,
    page_number: pageOffset,
  })
  const cacheKey = [
    "searchMerchantProducts",
    groupId,
    debouncedSearchTerm,
    pageSize,
    pageOffset,
  ]
  const query = () => apiRequest(`group/merchant_product_search?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId && debouncedSearchTerm?.length > 2,
  })
}

export function useSuggestedProducts({ productId }) {
  const paramString = qs.stringify({
    product_id: productId,
  })
  const cacheKey = ["suggestedProducts", productId]
  const query = () => apiRequest(`products/competitors?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!productId,
  })
}

/** ------------------- **/
/*  Ads
/** ------------------- **/

export function useGetAdAttributionTimeseries({ groupId, startDate, endDate }) {
  const cacheKey = ["ads", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/overview/timeseries?${paramString}`)
  return useQuery(cacheKey, query)
}
export function useGetAdAttribution({ groupId, startDate, endDate }) {
  const cacheKey = ["ads", groupId, startDate, endDate]
  const paramString = qs.stringify({
    group_id: groupId,
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`ads/overview?${paramString}`)
  return useQuery(cacheKey, query)
}

/** ------------------- **/
/*  Demand Planning
/** ------------------- **/
export function useGetDemandPlanning({ groupId }) {
  const cacheKey = ["demandPlanning"]
  const paramString = qs.stringify({
    group_id: groupId,
  })
  const query = () => apiRequest(`demand_planning?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId,
  })
}

/** ------------------- **/
/*  Integrations
/** ------------------- **/

export function useAuthFacebook() {
  return useMutation(
    ({ code, state }) => {
      state = encodeURI(state)
      return apiRequest(
        `group/facebook_login?code=${code}&state=${state}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("API token successfully generated")
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue logging in")
      },
    }
  )
}

export function useManualAuthFacebook() {
  return useMutation(
    ({ groupId, fbAdID }) => {
      return apiRequest(
        `group/facebook_login?group_id=${groupId}&fb_aid=${fbAdID}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Facebook advertising ID and will begin importing data"
        )
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue sending your Facebook advertising ID to our servers. Please try again later or contact support."
        )
      },
    }
  )
}

export function useAuthGoogle() {
  return useMutation(
    ({ code, state }) => {
      state = encodeURI(state)
      return apiRequest(
        `group/google_login?code=${code}&state=${state}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("API token successfully generated")
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue logging in")
      },
    }
  )
}

export function useInitAuthGoogle(group_id, integration) {
  const cacheKey = ["GoogleInit", group_id, integration]
  const query = () =>
    apiRequest(
      `group/google_login_init?group_id=${group_id}&integration_id=${integration}`,
      "GET"
    )
  return useQuery(cacheKey, query, { enabled: !!group_id })
}

export function useManualAuthGoogleAnalytics() {
  return useMutation(
    ({ groupId, gaViewID }) => {
      return apiRequest(
        `group/google_login?group_id=${groupId}&analytics_vid=${gaViewID}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Google Analytics view ID and will begin importing data"
        )
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue sending your Google Analytics view ID to our servers. Please try again later or contact support."
        )
      },
    }
  )
}

export function useRevokeIntegrationToken() {
  return useMutation(
    ({ group_id, integration, mode }) => {
      return apiRequest(
        `group/integrations_login_revoke?group_id=${group_id}&integration_id=${integration}&mode=${mode}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("Integration access successfully revoked")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue revoking your login")
      },
    }
  )
}

/** ------------------- **/
/*  Market Insights
/** ------------------- **/

export function useMarketInsights({ terms, startDate, endDate }) {
  const cacheKey = ["marketInsights", terms, startDate, endDate]
  const paramString = qs.stringify({
    terms: terms.join(","),
    start_date: startDate,
    end_date: endDate,
  })
  const query = () => apiRequest(`market_insights/explore?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!terms?.length,
  })
}

export function useMarketInsightsTerms() {
  const cacheKey = "marketInsightsTerms"
  const query = () => apiRequest("market_insights/terms")
  return useQuery(cacheKey, query)
}

export function useMarketInsightsTrends() {
  const cacheKey = "marketInsightsTrends"
  const query = () => apiRequest("market_insights/trends")
  return useQuery(cacheKey, query)
}

export function useMarketInsightsAll() {
  const cacheKey = "marketInsightsAll"
  const query = () => apiRequest("market_insights/all")
  return useQuery(cacheKey, query)
}
